import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  MapPin, 
  Star, 
  Clock, 
  Car, 
  Wifi,
  Coffee,
  Dumbbell,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Users
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VenueDetailPage = () => {
  const { venueId } = useParams();
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    const fetchVenue = async () => {
      try {
        const response = await fetch(`${API}/venues/${venueId}`);
        if (response.ok) {
          const data = await response.json();
          setVenue(data);
        } else {
          toast.error('Venue not found');
          navigate('/venues');
        }
      } catch (error) {
        console.error('Error fetching venue:', error);
        toast.error('Failed to load venue');
      } finally {
        setLoading(false);
      }
    };

    fetchVenue();
  }, [venueId, navigate]);

  const handleSubmitReview = async () => {
    if (!newReview.comment.trim()) {
      toast.error('Please write a review');
      return;
    }

    setSubmittingReview(true);
    try {
      const response = await fetch(`${API}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          venue_id: venueId,
          rating: newReview.rating,
          comment: newReview.comment
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Review submitted! +${data.points_earned} points`);
        setNewReview({ rating: 5, comment: '' });
        // Refresh venue to get updated reviews
        const venueResponse = await fetch(`${API}/venues/${venueId}`);
        if (venueResponse.ok) {
          setVenue(await venueResponse.json());
        }
      } else {
        toast.error('Failed to submit review');
      }
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const getAmenityIcon = (amenity) => {
    switch (amenity.toLowerCase()) {
      case 'parking': return Car;
      case 'wifi': return Wifi;
      case 'cafe': case 'restaurant': return Coffee;
      case 'gym': case 'fitness': return Dumbbell;
      default: return Star;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="h-96 bg-muted animate-pulse rounded-xl mb-6"></div>
          <div className="h-8 bg-muted animate-pulse rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-muted animate-pulse rounded w-2/3"></div>
        </div>
      </Layout>
    );
  }

  if (!venue) return null;

  const images = venue.photos?.length > 0 ? venue.photos : [
    'https://images.pexels.com/photos/14262635/pexels-photo-14262635.jpeg'
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="venue-detail">
        {/* Image Gallery */}
        <div className="relative rounded-2xl overflow-hidden mb-8 h-64 md:h-96">
          <img 
            src={images[currentImageIndex]} 
            alt={venue.name}
            className="w-full h-full object-cover"
          />
          {images.length > 1 && (
            <>
              <Button
                variant="secondary"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2"
                onClick={() => setCurrentImageIndex(i => i === 0 ? images.length - 1 : i - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2"
                onClick={() => setCurrentImageIndex(i => i === images.length - 1 ? 0 : i + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    className={`h-2 w-2 rounded-full transition-colors ${
                      idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                    onClick={() => setCurrentImageIndex(idx)}
                  />
                ))}
              </div>
            </>
          )}
          <div className="absolute top-4 left-4">
            <Badge className={venue.indoor ? 'bg-primary text-white' : 'bg-secondary text-secondary-foreground'}>
              {venue.indoor ? 'Indoor' : 'Outdoor'}
            </Badge>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="font-heading text-3xl font-bold mb-2">{venue.name}</h1>
                <p className="text-muted-foreground flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {venue.address}, {venue.city}
                </p>
              </div>
              <div className="text-right">
                <div className="flex items-center mb-1">
                  <Star className="h-5 w-5 text-yellow-500 mr-1" />
                  <span className="text-xl font-bold">{venue.rating}</span>
                </div>
                <p className="text-sm text-muted-foreground">{venue.review_count} reviews</p>
              </div>
            </div>

            <p className="text-muted-foreground mb-6">{venue.description}</p>

            <Tabs defaultValue="courts" className="space-y-4">
              <TabsList>
                <TabsTrigger value="courts" data-testid="tab-courts">Courts</TabsTrigger>
                <TabsTrigger value="amenities" data-testid="tab-amenities">Amenities</TabsTrigger>
                <TabsTrigger value="rules" data-testid="tab-rules">Rules</TabsTrigger>
                <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
              </TabsList>

              <TabsContent value="courts" className="space-y-4">
                {venue.courts?.map((court) => (
                  <Card key={court.court_id} data-testid={`court-${court.court_id}`}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">{court.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {court.court_type} • {court.surface}
                        </p>
                        <div className="flex gap-2 mt-2">
                          {court.indoor && <Badge variant="outline">Indoor</Badge>}
                          {court.lighting && <Badge variant="outline">Lighting</Badge>}
                        </div>
                      </div>
                      <Button 
                        onClick={() => navigate(`/book/${venue.venue_id}?court=${court.court_id}`)}
                        className="btn-primary"
                        data-testid={`book-court-${court.court_id}`}
                      >
                        Book Now
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>

              <TabsContent value="amenities">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {venue.amenities?.map((amenity, idx) => {
                    const Icon = getAmenityIcon(amenity);
                    return (
                      <div key={idx} className="flex items-center p-3 bg-muted rounded-lg">
                        <Icon className="h-5 w-5 mr-3 text-primary" />
                        <span>{amenity}</span>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="rules">
                <ul className="space-y-2">
                  {venue.rules?.map((rule, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="h-2 w-2 rounded-full bg-primary mt-2 mr-3 flex-shrink-0"></span>
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </TabsContent>

              <TabsContent value="reviews" className="space-y-6">
                {/* Add Review */}
                {isAuthenticated && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Write a Review</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-1 mb-4">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setNewReview(r => ({ ...r, rating: star }))}
                            className="focus:outline-none"
                            data-testid={`star-${star}`}
                          >
                            <Star 
                              className={`h-6 w-6 ${
                                star <= newReview.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'
                              }`} 
                            />
                          </button>
                        ))}
                      </div>
                      <Textarea
                        placeholder="Share your experience..."
                        value={newReview.comment}
                        onChange={(e) => setNewReview(r => ({ ...r, comment: e.target.value }))}
                        className="mb-4"
                        data-testid="review-input"
                      />
                      <Button 
                        onClick={handleSubmitReview} 
                        disabled={submittingReview}
                        data-testid="submit-review-btn"
                      >
                        {submittingReview ? 'Submitting...' : 'Submit Review'}
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Existing Reviews */}
                <div className="space-y-4">
                  {venue.reviews?.length > 0 ? venue.reviews.map((review) => (
                    <Card key={review.review_id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{review.user_name}</span>
                          <div className="flex items-center">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star 
                                key={i} 
                                className={`h-4 w-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-muted-foreground">{review.comment}</p>
                      </CardContent>
                    </Card>
                  )) : (
                    <p className="text-center text-muted-foreground py-8">No reviews yet. Be the first!</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Booking Card */}
          <div>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <p className="text-3xl font-heading font-bold text-primary">₱{venue.price_per_hour}</p>
                  <p className="text-muted-foreground">per hour</p>
                </div>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center text-sm">
                    <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>{venue.courts?.length || 0} courts available</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>Open 6:00 AM - 10:00 PM</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>Max 4 players per court</span>
                  </div>
                </div>

                <Button 
                  onClick={() => navigate(`/book/${venue.venue_id}`)}
                  className="w-full btn-primary text-lg py-6"
                  data-testid="book-venue-btn"
                >
                  Book Now
                </Button>

                <p className="text-xs text-center text-muted-foreground mt-4">
                  Free cancellation up to 24 hours before
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default VenueDetailPage;
